import { useState } from 'react'
import Modal from '../components/Modal'
import { ErrorText, Field, Row, Text } from '../components/Form'
import { createPerson, updatePerson } from '../lib/artists'
import type { PersonDraft } from '../lib/artists'
import type { Person, WithId } from '../types/models'

/**
 * Someone who travels.
 *
 * Stored at ORGANISATION level, not on an artist and not on a tour. The same
 * FOH engineer works for three acts on the roster, and their passport should
 * be entered once — which is the whole reason this record exists separately
 * from `tourCrew`.
 */
export default function PersonDialog({
  orgId,
  person,
  onClose,
  onCreated,
}: {
  orgId: string
  person?: WithId<Person>
  onClose: () => void
  onCreated?: (personId: string) => void
}) {
  const [draft, setDraft] = useState<PersonDraft>(() => ({
    name: person?.name ?? '',
    email: person?.email ?? '',
    phone: person?.phone ?? '',
    nationality: person?.nationality ?? '',
    dietaryRequirements: person?.dietaryRequirements ?? '',
    allergies: person?.allergies ?? '',
    roomPreference: person?.roomPreference ?? '',
    seatPreference: person?.seatPreference ?? '',
  }))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const patch = (next: Partial<PersonDraft>) => setDraft((current) => ({ ...current, ...next }))

  return (
    <Modal
      open
      title={person ? 'Edit person' : 'Add person'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!draft.name.trim() || busy}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                if (person) {
                  await updatePerson(person.id, draft)
                } else {
                  const id = await createPerson(draft, orgId)
                  onCreated?.(id)
                }
                onClose()
              } catch (caught) {
                setError((caught as Error).message)
                setBusy(false)
              }
            }}
          >
            {busy ? 'Saving…' : person ? 'Save' : 'Add person'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name">
          <Text value={draft.name} onChange={(value) => patch({ name: value })} autoFocus />
        </Field>

        <Row>
          <Field label="Email">
            <Text
              type="email"
              value={draft.email ?? ''}
              onChange={(value) => patch({ email: value })}
            />
          </Field>
          <Field label="Phone">
            <Text value={draft.phone ?? ''} onChange={(value) => patch({ phone: value })} />
          </Field>
        </Row>

        <Field label="Nationality" hint="Used to flag visa requirements on international dates.">
          <Text
            value={draft.nationality ?? ''}
            onChange={(value) => patch({ nationality: value })}
          />
        </Field>

        <Row>
          <Field label="Dietary requirements">
            <Text
              value={draft.dietaryRequirements ?? ''}
              onChange={(value) => patch({ dietaryRequirements: value })}
            />
          </Field>
          <Field label="Allergies">
            <Text
              value={draft.allergies ?? ''}
              onChange={(value) => patch({ allergies: value })}
            />
          </Field>
        </Row>

        <Row>
          <Field label="Room preference">
            <Text
              value={draft.roomPreference ?? ''}
              onChange={(value) => patch({ roomPreference: value })}
            />
          </Field>
          <Field label="Seat preference">
            <Text
              value={draft.seatPreference ?? ''}
              onChange={(value) => patch({ seatPreference: value })}
            />
          </Field>
        </Row>

        <p className="t-caption">
          Passport details live on this record too, and are only ever readable inside your
          organisation. Add them from the person's page once it exists.
        </p>

        <ErrorText>{error}</ErrorText>
      </div>
    </Modal>
  )
}
